/**
 * Nieuw contact starten (sessie 72, punt 10): de knoppen Mail en WhatsApp
 * in het klantblok van de zaak en "Nieuw bericht" bovenaan Vandaag. Beide
 * zetten via de bestaande route (`POST /api/werkplek/zaken/nieuw`) een zaak
 * klaar die de werkplek net zo behandelt als een inkomende, en openen hem
 * met het antwoordvak in het gekozen kanaal. Er wordt niets verstuurd.
 */
import { useMutation, useQueryClient } from "@tanstack/react-query";

import { vertaalApiFout } from "@/lib/api";

import { KANALEN, v6Api, type Kanaal } from "./api";
import { useV6 } from "./context";
import { v6Sleutels } from "./sleutels";

export type NieuwKanaal = "mail" | "whatsapp";
export type NieuwBron = "vandaag" | "klantblok" | "klantpagina";

export function useNieuwBericht(bron: NieuwBron) {
  const { kies } = useV6();
  const queryClient = useQueryClient();
  const start = useMutation({
    mutationFn: (v: { klantId: string; kanaal: NieuwKanaal }) =>
      v6Api.nieuwBericht({ klant_id: v.klantId, kanaal: v.kanaal, bron }),
    onSuccess: (uitkomst, v) => {
      if (!uitkomst.zaak_id) return;
      void queryClient.invalidateQueries({ queryKey: v6Sleutels.lijst });
      kies(uitkomst.zaak_id, v.kanaal);
    },
  });
  return {
    /** Geeft de uitkomst terug, zodat een paneel zich na afloop sluit. */
    start: (klantId: string, kanaal: NieuwKanaal) => start.mutateAsync({ klantId, kanaal }),
    bezig: start.isPending,
    fout: start.isError ? vertaalApiFout(start.error) : null,
  };
}

/**
 * Het kanaal waarin het antwoordvak van de zaak uit de url opent
 * (`?zaak=<id>&vak=<kanaal>`). Bewust niet `?kanaal=`: dat is de
 * kanaaltegel van de lijst (sessie 70); die twee mogen elkaar niet raken.
 */
export function vakUitUrl(params: URLSearchParams): Kanaal | null {
  const vak = params.get("vak");
  return params.get("zaak") && KANALEN.includes(vak as Kanaal) ? (vak as Kanaal) : null;
}
